import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import { db } from "../firebase.js";
import DecorZoomStore from "../pages/DecorZoomStore.js";
import ListingMediaPreview from "./ListingMediaPreview.jsx";
import {
  buildPathWithFirstBirthdayTestMode,
  getFirstBirthdayTestModeProducts,
  isFirstBirthdayTestModeEnabled,
} from "../utils/firstBirthdayTestMode.js";
import { buildPathWithBranch, isAllahabadBranch, resolveWhatsappNumber } from "../utils/branchWhatsapp.js";
import { getListingBadges } from "../utils/listingBadges.js";
import {
  getProductOrderId,
  getRecentlyViewedProductMap,
  getSessionStableProductOrder,
} from "../utils/listingSessionStorage.js";

const fallbackImage = "https://via.placeholder.com/150?text=Image+Not+Found";

const fetchProductsSnapshot = async (decodedSubcat) => {
  const productsRef = collection(db, "PRODUCTS MAIN");
  const target = decodedSubcat.toLowerCase();

  try {
    if (target === "activities") {
      return await getDocs(query(productsRef, where("mainCategory", "==", "Activities")));
    }

    if (target === "first birthday decorations") {
      return await getDocs(
        query(
          productsRef,
          where("mainCategory", "==", "First Birthday Decorations"),
          where("subcategory", "==", "Banquet Decor")
        )
      );
    }

    if (target === "home decor") {
      return await getDocs(
        query(
          productsRef,
          where("mainCategory", "==", "First Birthday Decorations"),
          where("subcategory", "==", "Home Decor")
        )
      );
    }

    if (target === "cafe decor" || target === "cafe") {
      return await getDocs(
        query(
          productsRef,
          where("mainCategory", "==", "First Birthday Decorations"),
          where("subcategory", "==", "Cafe")
        )
      );
    }

    return await getDocs(query(productsRef, where("subcategory", "==", decodedSubcat)));
  } catch (error) {
    return await getDocs(productsRef);
  }
};

const mainCategoriesWithSubcategories = {
  "Wedding Events": [
    "Bride Entry",
    "Mirror Entry",
    "Haldi Decor",
    "Mehndi Decor",
    "Flower Shower",
    "Cold Pyro Fog",
    "Cold Pyro Gun",
    "Confetti Blaster",
    "Mines",
  ],
  "Theme Cakes": ["Jungle Theme", "Barbie Theme", "Superhero Theme"],
};

const launchingSoonSubcategories = [
  "Bride Entry",
  "Mirror Entry",
  "Haldi Decor",
  "Mehndi Decor",
  "Flower Shower",
  "Cold Pyro Fog",
  "Cold Pyro Gun",
  "Confetti Blaster",
  "Mines",
  "Magic Show",
  "Game Coordinator",
  "Tattoo Artist",
  "Bubble Show",
  "Cartoon Characters",
  "Customized Key Rings",
  "Mascot Dress",
  "Jungle Theme",
  "Barbie Theme",
  "Superhero Theme",
  "Birthday Photo Shoot",
  "Theme Cake Tables",
];

const SubcategoryPage = () => {
  const { subcategory } = useParams();
  const navigate = useNavigate();
  const [groupedProducts, setGroupedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewedProducts, setViewedProducts] = useState(() => getRecentlyViewedProductMap());

  const slug = subcategory;
  const decodedSubcat = slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const isMainCategory = mainCategoriesWithSubcategories.hasOwnProperty(decodedSubcat);
  const isLaunchingSoon = launchingSoonSubcategories.includes(decodedSubcat);
  const isDecorZoomStore = decodedSubcat === "DecorZoom Store";
  const isFirstBirthdayLocalTestMode =
    typeof window !== "undefined" &&
    isFirstBirthdayTestModeEnabled(window.location.search, window.location.hostname);
  const locationSearch = typeof window !== "undefined" ? window.location.search : "";
  const isAllahabadVisit = isAllahabadBranch(locationSearch);
  const whatsappNumber = resolveWhatsappNumber("918377982191", locationSearch);
  const canonicalUrl = `https://www.decorzoom.in/subcategory/${slug}`;
  const pageTitle = `${decodedSubcat} | DecorZoom`;
  const pageDescription =
    decodedSubcat === "First Birthday Decorations"
      ? "Browse first birthday decoration products from DecorZoom, including banquet-style setups for birthday celebrations in Delhi NCR."
      : decodedSubcat === "Home Decor"
      ? "Browse home decoration products from DecorZoom for birthdays and family celebrations."
      : decodedSubcat === "Cafe Decor"
      ? "Browse cafe decoration products from DecorZoom for birthday parties and small event setups."
      : `Browse ${decodedSubcat} decoration products and services from DecorZoom.`;

  useEffect(() => {
    const refreshViewedProducts = () => {
      setViewedProducts(getRecentlyViewedProductMap());
    };

    refreshViewedProducts();
    window.addEventListener("focus", refreshViewedProducts);

    return () => {
      window.removeEventListener("focus", refreshViewedProducts);
    };
  }, [slug]);

  useEffect(() => {
    if (isMainCategory || isLaunchingSoon || isDecorZoomStore) {
      setLoading(false);
      return;
    }

    setLoading(true);

    (async () => {
      try {
        if (isFirstBirthdayLocalTestMode && decodedSubcat === "First Birthday Decorations") {
          const localTestProducts = getFirstBirthdayTestModeProducts().filter((product) => {
            const productSubcat = (product.subcategory || product.subCategory || "").toLowerCase();
            const mainCat = (product.mainCategory || product.maincategory || "").toLowerCase();

            return mainCat === "first birthday decorations" && productSubcat === "banquet decor";
          });

          setGroupedProducts(getSessionStableProductOrder(localTestProducts, slug));
          setLoading(false);
          return;
        }

        const snapshot = await fetchProductsSnapshot(decodedSubcat);
        const allProducts = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const filtered = allProducts.filter((product) => {
          const productSubcat = (product.subcategory || product.subCategory || "").toLowerCase();
          const mainCat = (product.mainCategory || product.maincategory || "").toLowerCase();
          const target = decodedSubcat.toLowerCase();

          if (target === "activities") {
            return mainCat === "activities";
          }

          if (target === "first birthday decorations") {
            return mainCat === "first birthday decorations" && productSubcat === "banquet decor";
          }

          if (mainCat === "first birthday decorations") {
            if (target === "home decor") return productSubcat === "home decor";
            if (target === "cafe decor" || target === "cafe") return productSubcat === "cafe";
            return false;
          }

          return productSubcat === target;
        });

        setGroupedProducts(getSessionStableProductOrder(filtered, slug));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    })();
  }, [decodedSubcat, isMainCategory, isLaunchingSoon, isDecorZoomStore, isFirstBirthdayLocalTestMode, slug]);

  if (isDecorZoomStore) {
    return <DecorZoomStore />;
  }

  const filteredProducts = groupedProducts.filter((product) =>
    (product.title || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: decodedSubcat,
            description: pageDescription,
            url: canonicalUrl,
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.decorzoom.in/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: decodedSubcat,
                item: canonicalUrl,
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="mb-4">
        <button
          onClick={() => navigate("/")}
          className="text-green-600 font-semibold hover:underline flex items-center gap-2 text-sm sm:text-base"
        >
          ← Back to Home
        </button>
      </div>

      <h1 className="text-3xl sm:text-4xl font-extrabold mb-4 text-center">{subcategory}</h1>

      {!isMainCategory && !isLaunchingSoon && (
        <div className="w-full mb-6">
          {isFirstBirthdayLocalTestMode && decodedSubcat === "First Birthday Decorations" && (
            <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
              First Birthday TEST MODE is active on localhost. This page is showing the local test dataset only.
            </div>
          )}
          <input
            type="text"
            placeholder="Search your themes.."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-4 border-2 border-green-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-base sm:text-lg shadow-md"
          />
        </div>
      )}

      {isMainCategory ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {mainCategoriesWithSubcategories[decodedSubcat].map((item, idx) => (
            <div key={idx} className="flex flex-col items-center">
              <div
                onClick={() => {
                  const nextSlug =
                    typeof item === "string" ? item.toLowerCase().replace(/\s+/g, "-") : item.slug;
                  navigate(`/subcategory/${nextSlug}`);
                }}
                className="relative w-full bg-white aspect-square rounded-xl sm:rounded-3xl overflow-hidden shadow-md sm:shadow-lg hover:shadow-xl sm:hover:shadow-2xl transform transition-transform hover:scale-105 cursor-pointer"
              >
                <img
                  src="/images/product-card-background.webp"
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-20 rounded-xl sm:rounded-3xl"
                />
                <div className="relative w-full h-full flex items-center justify-center p-1 sm:p-2">
                  <img
                    src={`/images/${
                      typeof item === "string" ? item.replaceAll(" ", "-").toLowerCase() : item.imageKey
                    }-1.webp`}
                    alt={typeof item === "string" ? item : item.label}
                    loading="lazy"
                    className="h-[93%] w-[93%] sm:h-[90%] sm:w-[90%] object-contain rounded-2xl"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : loading ? (
        <div className="text-center text-lg text-green-600 font-semibold mt-10">Loading...</div>
      ) : isLaunchingSoon ? (
        <div className="flex flex-col items-center justify-center mt-12 space-y-6">
          <p className="text-lg sm:text-xl text-center text-gray-700 font-semibold">
            We are working on it. It will be resolved soon.
            <br />
            Tap WhatsApp below for photos, videos and queries.
          </p>
          <a
            href={`https://wa.me/${whatsappNumber}?text=Hi,%20I'm%20interested%20in%20${encodeURIComponent(
              decodedSubcat
            )}%20decoration.%20Please%20send%20photos%20and%20videos.`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-full shadow-lg text-lg"
          >
            📞 WhatsApp Now
          </a>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center text-lg text-gray-500 mt-10">No matching products found.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mt-8">
          {filteredProducts.map((product) => {
            const firstPackage = product.packages?.[0] || {};
            const badges = getListingBadges(product);
            const viewedAt = viewedProducts.get(getProductOrderId(product));
            const isViewed = Boolean(viewedAt);
            const viewedLabel =
              viewedAt && Date.now() - viewedAt < 30 * 60 * 1000
                ? "Just Watched"
                : "Viewed";
            const rawThumbVideo =
              Array.isArray(firstPackage.videos) && firstPackage.videos.length > 0
                ? firstPackage.videos[0]
                : Array.isArray(product.videos) && product.videos.length > 0
                ? product.videos[0]
                : null;

            const thumbVideo = rawThumbVideo
              ? rawThumbVideo.startsWith("http") || rawThumbVideo.startsWith("/")
                ? rawThumbVideo
                : `/${rawThumbVideo}`
              : null;

            const thumbImage =
              Array.isArray(firstPackage.images) && firstPackage.images.length > 0
                ? firstPackage.images[0]
                : Array.isArray(product.images) && product.images.length > 0
                ? product.images[0]
                : fallbackImage;

            const altText =
              firstPackage.altTexts?.[0] ||
              product.altTexts?.[0] ||
              product.title ||
              "DecorZoom Product";

            return (
              <div
                key={product.id}
                onClick={() => {
                  const productSlug = product.slug || product.title.toLowerCase().replace(/\s+/g, "-");
                  navigate(
                    buildPathWithBranch(
                      buildPathWithFirstBirthdayTestMode(
                        `/product/${productSlug}--${product.id}`,
                        isFirstBirthdayLocalTestMode && decodedSubcat === "First Birthday Decorations"
                      ),
                      isAllahabadVisit
                    )
                  );
                }}
                className={`p-2 sm:p-4 bg-white rounded-2xl shadow-md hover:shadow-xl transition-transform transform hover:scale-105 text-center cursor-pointer relative overflow-hidden ${
                  isViewed ? "ring-1 ring-emerald-200 bg-emerald-50/40" : ""
                }`}
              >
                <img
                  src="/images/product-card-background.webp"
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-20 rounded-2xl"
                />

                {badges.length > 0 && (
                  <div className="absolute left-2 top-2 z-20 flex flex-wrap gap-2">
                    {badges.map((badge) => (
                      <span
                        key={badge.key}
                        className={`rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide shadow-sm ${
                          badge.tone === "trending"
                            ? "bg-amber-500 text-white"
                            : "bg-emerald-600 text-white"
                        }`}
                      >
                        {badge.label}
                      </span>
                    ))}
                  </div>
                )}

                {isViewed && (
                  <div className="absolute right-2 top-2 z-20">
                    <span className="rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-bold text-emerald-700 shadow-sm ring-1 ring-emerald-200 sm:px-2.5 sm:py-1">
                      {viewedLabel}
                    </span>
                  </div>
                )}

                <div className="relative z-10">
                  <ListingMediaPreview
                    videoSrc={thumbVideo}
                    posterSrc={thumbImage}
                    fallbackSrc={fallbackImage}
                    alt={altText}
                    className="w-32 sm:w-36 md:w-48 h-32 sm:h-36 md:h-48 rounded-full mx-auto mb-2 sm:mb-4"
                  />

                  <p className="text-xs sm:text-base font-semibold mb-1 line-clamp-2">
                    {product.title}
                  </p>

                  <div className="text-center mt-2">
                    <p className="font-bold text-green-700">Click image for more details</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubcategoryPage;
